import React, { useEffect, useState } from 'react';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl,
    useIntl,
} from 'react-intl';
import { IModalChildProps } from '../../../common/modal';
import update from 'immutability-helper';
import {
    IAddItem,
    ILineItemAdd,
    IOrderDetails,
    IRelatedItems,
} from '../../../../models/order';
import './modal-styles.css';
import { RootState } from '../../../../store';
import { connect } from 'react-redux';
import {
    loadOrder,
    clearRelatedItems,
    addItemsFromLine,
    clearAddItemsCall,
    postLineToGetRelatedItems,
} from '../../../../store/order/order-actions';
import { setIsBusy } from '../../../../store/api/api-status-action';
import { IITAPISResult } from '../../../../models/result-models';
import { orderHold } from '../../../../api/order-api';
import RelatedItemsList from './related-items-list';
import ModalLarge from '../../../common/modal-large';
import { gaEvent } from '../../../../hooks/ga-hook';

interface IAddItemModal extends IModalChildProps {
    order: IOrderDetails;
    relatedItems: IRelatedItems | null;
    addItemsFromLine: (line: ILineItemAdd) => Promise<void>;
    addItemsResponse: IITAPISResult | null;
    postLineToGetRelatedItems: (parts: string[]) => Promise<void>;
    clearAddItemsCall: () => { type: string };
}

const AddItemModal = ({
    onAccept,
    onClose,
    order,
    relatedItems,
    addItemsFromLine,
    addItemsResponse,
    postLineToGetRelatedItems,
    clearAddItemsCall,
}: IAddItemModal) => {
    const intl = useIntl();
    let partNumber: string;
    const parts: string[] = [];
    const [items, setItems] = useState<ILineItemAdd>({
        orderNumber: order.orderNumber,
        lineItems: [],
    });
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);
    const [added, setAdded] = useState(false);
    const [options, setOptions] = useState<IAddItem[]>([]);
    const startingMinutes = 30;
    const [timeLeft, setTimeLeft] = useState(startingMinutes * 60);
    const [minutes, setMinutes] = useState(timeLeft / 60);
    const [seconds, setSeconds] = useState(timeLeft % 60);

    useEffect(() => {
        // exit early when we reach 0
        if (!timeLeft) {
            orderHold(order.orderNumber, false);
            onClose();
            return;
        }

        // save intervalId to clear the interval when the
        // component re-renders
        const intervalId = setInterval(() => {
            setTimeLeft(timeLeft - 1);
            setMinutes(Math.floor(timeLeft / 60));
            setSeconds(timeLeft % 60);
        }, 1000);

        // clear interval on re-render to avoid memory leaks
        return () => clearInterval(intervalId);
        // add timeLeft as a dependency to re-rerun the effect
        // when we update it
    }, [timeLeft]);
    useEffect(() => {
        if (!addItemsResponse) return;

        if (addItemsResponse!.returnCodeType === 'Error') {
            setError(true);
        } else {
            setSuccess(true);
        }
    }, [addItemsResponse]);
    useEffect(() => {
        if (!relatedItems) {
            order.lineItems.map((item) => {
                if (item.shippableFlag === 'Y') {
                    partNumber = item.partNumber;
                    parts.push(partNumber);
                }
                return parts;
            });
            postLineToGetRelatedItems(parts);
        }
        orderHold(order.orderNumber, true);
    }, []);
    useEffect(() => {
        let filteredResults: IAddItem[] = [];
        filteredResults = relatedItems!.relatedItems.filter(
            (item, index, self) =>
                index ===
                self.findIndex((t) => t.partNumber === item.partNumber)
        );
        setOptions(filteredResults);
    }, []);
    useEffect(() => {
        return () => {
            setSuccess(false);
            setError(false);
            clearAddItemsCall();
        };
    }, []);

    const handleAddDeleteAccessory = (partNumber: string, quantity: number) => {
        const line = options!.find(
            (option) => option.partNumber === partNumber
        ) as IAddItem;
        const index = items!.lineItems.findIndex(
            (x) => x.partNumber === partNumber
        );

        if (quantity <= 0) {
            setItems({
                ...items,

                lineItems: update(items.lineItems, {
                    $splice: [[index, 1]],
                }),
            });
        } else {
            if (index < 0) {
                setItems({
                    ...items,
                    lineItems: [
                        ...items.lineItems,
                        {
                            ...line,
                            quantity: quantity,
                        },
                    ],
                });
            } else {
                setItems({
                    ...items,
                    lineItems: update(items.lineItems, {
                        [index]: {
                            $set: {
                                ...items.lineItems[index],
                                quantity: quantity,
                            },
                        },
                    }),
                });
            }
        }
    };

    return (
        <ModalLarge>
            <>
                {relatedItems && options && (
                    <>
                        <div className="modal-header text-center">
                            {!success && !error && (
                                <h2 className="h2">
                                    Please select from eligible items below
                                </h2>
                            )}
                            <button
                                type="button"
                                className="close"
                                data-dismiss="modal"
                                aria-label={intl.formatMessage({
                                    id: 'Close',
                                })}
                                onClick={onClose}
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>

                        {!success && !error && !added && (
                            <>
                                <div className="text-center">
                                    <p>
                                        <FormattedMessage
                                            id="You_have"
                                            defaultMessage="You have"
                                        />{' '}
                                        <>{minutes}</>
                                        {':'}
                                        <>
                                            {seconds < 10
                                                ? '0' + seconds
                                                : seconds}
                                        </>{' '}
                                        <FormattedMessage
                                            id="to_complete_this_transaction"
                                            defaultMessage="to complete this transaction"
                                        />
                                    </p>
                                    <p>
                                        <i>
                                            <FormattedMessage
                                                id="Need_to_add_message"
                                                defaultMessage="Need to add something that’s not
                                            listed below? You can still cancel
                                            and re-order!"
                                            />
                                        </i>
                                    </p>
                                </div>
                                <div className="add-item-list">
                                    {options.map((item, i) => {
                                        const selectedAccessory =
                                            items.lineItems.find(
                                                (x) =>
                                                    x.partNumber ===
                                                    item.partNumber
                                            );
                                        return (
                                            <React.Fragment key={i}>
                                                <RelatedItemsList
                                                    item={item}
                                                    order={order}
                                                    handleAddDeleteAccessory={
                                                        handleAddDeleteAccessory
                                                    }
                                                    quantity={
                                                        selectedAccessory
                                                            ? selectedAccessory!
                                                                  .quantity
                                                            : 0
                                                    }
                                                />
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                        {!success && !error && added && (
                            <>
                                <p className="text-center modal-spacing">
                                    <FormattedMessage
                                        id="Updating_may_cause_changes_message"
                                        defaultMessage="Updating your order may change your expected
                                    ship date. We will notify you if your
                                    expected ship date changes."
                                    />
                                </p>
                                <p className="text-center modal-spacing">
                                    <strong className="text-center modal-spacing">
                                        <FormattedMessage
                                            id="Are_you_sure_you_want_to_continue"
                                            defaultMessage="Are you sure you want to continue?"
                                        />
                                    </strong>
                                </p>
                            </>
                        )}
                        {!success && error && (
                            <p className="text-center modal-spacing">
                                <FormattedMessage
                                    id="We're_sorry_there's_been_an_error"
                                    defaultMessage="We're sorry there's been an error. Please try
                                again."
                                />
                            </p>
                        )}
                        {success && !error && (
                            <p className="text-center">
                                <span className="fa fa-check icon-green"></span>
                                <strong>
                                    <FormattedMessage
                                        id="Item(s)_have_successfully_been_added"
                                        defaultMessage="Item(s) have successfully been added"
                                    />
                                </strong>
                            </p>
                        )}
                        <div className="modal-footer">
                            {!success && !error && !added && (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-default"
                                        onClick={onClose}
                                    >
                                        <FormattedMessage
                                            id="Cancel"
                                            defaultMessage="Cancel"
                                        />
                                    </button>
                                    <button
                                        className="btn btn-info"
                                        onClick={() => {
                                            setAdded(true);
                                        }}
                                    >
                                        <FormattedMessage
                                            id="Update_Order"
                                            defaultMessage="Update Order"
                                        />
                                    </button>
                                </>
                            )}
                            {!success && !error && added && (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-default"
                                        onClick={onClose}
                                    >
                                        <FormattedMessage
                                            id="Cancel"
                                            defaultMessage="Cancel"
                                        />
                                    </button>
                                    <button
                                        className="btn btn-info"
                                        id=""
                                        onClick={() => {
                                            addItemsFromLine(items);
                                            gaEvent(
                                                'Estore_Order_Updates',
                                                'AddItem_Modal',
                                                order.billTo.accountNumber
                                            );
                                        }}
                                    >
                                        <FormattedMessage
                                            id="Confirm"
                                            defaultMessage="Confirm"
                                        />
                                    </button>
                                </>
                            )}
                            {!success && error && (
                                <>
                                    <button
                                        name="add-item-cancel-if-error"
                                        type="button"
                                        className="btn btn-default"
                                        onClick={onClose}
                                    >
                                        <FormattedMessage
                                            id="Cancel"
                                            defaultMessage="Cancel"
                                        />
                                    </button>
                                    <button
                                        name="add-item-retry-button"
                                        type="button"
                                        className="btn btn-default"
                                        onClick={() => {
                                            setError(false);
                                            setAdded(false);
                                            setItems({
                                                orderNumber: order.orderNumber,
                                                lineItems: [],
                                            });
                                        }}
                                    >
                                        <FormattedMessage
                                            id="Retry"
                                            defaultMessage="Retry"
                                        />
                                    </button>
                                </>
                            )}
                            {success && !error && (
                                <button
                                    name="add-item-close"
                                    type="button"
                                    className="btn btn-default"
                                    onClick={onAccept}
                                >
                                    <FormattedMessage
                                        id="Close"
                                        defaultMessage="Close"
                                    />
                                </button>
                            )}
                        </div>
                    </>
                )}
            </>
        </ModalLarge>
    );
};
const mapStateToProps = (state: RootState) => {
    return {
        addItemsResponse: state.addItemsResponse
            ? state.addItemsResponse.data!
            : null,
        relatedItems: state.relatedItems ? state.relatedItems.data! : null,
    };
};

const mapDispatchToProps = {
    loadOrder,
    setIsBusy,
    clearRelatedItems,
    addItemsFromLine,
    clearAddItemsCall,
    postLineToGetRelatedItems,
};

const AddItemModalContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(AddItemModal);

export default AddItemModalContainer;
